/**
 * StoreSetupScreen — creates the merchant's first store.
 *
 * Reached when the merchant is APPROVED but has no store yet. The store is
 * written into the local database and travels to the server on the next
 * `/api/sync/push`, exactly as on Mobile and Desktop — see provisionStore().
 *
 * Publishing that store is a three-step sequence whose order matters, and it
 * lives in bootstrapStoreOnServer() rather than here — see that module for why
 * a second round trip is needed before the till can bill.
 */
import { useState, type FormEvent } from 'react';
import { useAppStore } from '@/store/useAppStore';
import { isValidGstin, GST_STATE_CODES } from '@/reports/gst/gstStates';
import { provisionStore } from '@/onboarding/provisionStore';
import { bootstrapStoreOnServer } from '@/onboarding/bootstrapStore';
import { Field, OnboardingCard, PRIMARY, QUIET, LABEL, FIELD } from './OnboardingCard';

/** Code → name, sorted by name; `store.state` holds the NAME (see gst/storeIdentity). */
const STATES = Object.entries(GST_STATE_CODES)
  .map(([code, name]) => ({ code, name }))
  .sort((a, b) => a.name.localeCompare(b.name));

export function StoreSetupScreen({ onLogout }: { onLogout: () => void }) {
  const currentUser = useAppStore((s) => s.currentUser);

  const [name, setName] = useState('');
  const [state, setState] = useState('');
  const [city, setCity] = useState('');
  const [address, setAddress] = useState('');
  const [pincode, setPincode] = useState('');
  const [gstin, setGstin] = useState('');
  const [invoicePrefix, setInvoicePrefix] = useState('INV-');
  const [taxInclusive, setTaxInclusive] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const gstinBad = gstin.trim().length > 0 && !isValidGstin(gstin);
  const canSubmit = name.trim() && state && !gstinBad && !busy;

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!canSubmit) return;

    setBusy(true);
    setError(null);
    try {
      await provisionStore({
        name,
        state,
        city,
        address,
        pincode,
        gstin,
        invoicePrefix,
        taxInclusive,
      });

      // Register the device, publish the store, then claim a billing slot — in
      // that order, because each step needs the previous one on the server. See
      // bootstrapStoreOnServer(); getting this wrong is what made a new
      // merchant's first sale fail with "device is not activated for billing".
      //
      // Intentionally not awaited here. `provisionStore` has already set the
      // active store, so this screen is about to unmount; the bootstrap lives at
      // module scope so it runs to completion regardless, and reports trouble
      // through the shell's sync-error banner rather than to a dead component.
      void bootstrapStoreOnServer();

      // No navigation call: setting activeStoreId inside provisionStore is what
      // the gate in App.tsx watches, so the app shell mounts on the next render.
    } catch (err) {
      setError((err as Error)?.message ?? 'Could not create the store.');
      setBusy(false);
    }
  };

  return (
    <OnboardingCard
      width="lg"
      title="Set up your store"
      subtitle="You're approved. This is the counter you'll bill from."
      error={error}
      footer={
        <button type="button" onClick={onLogout} className={`${QUIET} w-full`}>
          Sign out
        </button>
      }
    >
      <form onSubmit={onSubmit} className="flex flex-col gap-4">
        <Field
          id="store-name"
          label="Store name"
          required
          autoFocus
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Patel Traders — Main Branch"
        />

        <div className="flex flex-col gap-1.5">
          <label htmlFor="store-state" className={LABEL}>
            State<span className="ml-1 text-danger-ink">*</span>
          </label>
          <select
            id="store-state"
            value={state}
            onChange={(e) => setState(e.target.value)}
            className={FIELD}
          >
            <option value="">Select state</option>
            {STATES.map((s) => (
              <option key={s.code} value={s.name} className="bg-ink-900 text-white">
                {s.code} — {s.name}
              </option>
            ))}
          </select>
          {/* Not a formality: place of supply decides CGST+SGST versus IGST on
              every invoice. A missing or wrong home state mis-splits the tax on
              every sale, and the error is only visible at return-filing time. */}
          <p className="text-label text-fg-tertiary">
            Decides CGST/SGST vs IGST on every bill. Required.
          </p>
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          <Field
            id="store-city"
            label="City"
            value={city}
            onChange={(e) => setCity(e.target.value)}
            placeholder="Surat"
          />
          <Field
            id="store-pincode"
            label="PIN code"
            inputMode="numeric"
            maxLength={6}
            value={pincode}
            onChange={(e) => setPincode(e.target.value.replace(/\D/g, ''))}
            placeholder="395001"
          />
        </div>

        <Field
          id="store-address"
          label="Address"
          value={address}
          onChange={(e) => setAddress(e.target.value)}
          placeholder="12, Ring Road"
        />

        <div className="grid gap-4 sm:grid-cols-2">
          <div className="flex flex-col gap-1.5">
            <label htmlFor="store-gstin" className={LABEL}>
              Store GSTIN
            </label>
            <input
              id="store-gstin"
              value={gstin}
              onChange={(e) => setGstin(e.target.value.toUpperCase())}
              className={FIELD}
              placeholder="22AAAAA0000A1Z5"
              maxLength={15}
              aria-invalid={gstinBad}
            />
            <p className={`text-label ${gstinBad ? 'text-danger-ink' : 'text-fg-tertiary'}`}>
              {gstinBad ? 'That is not a valid GSTIN.' : 'Optional. Overrides the business GSTIN.'}
            </p>
          </div>

          <Field
            id="store-prefix"
            label="Invoice prefix"
            value={invoicePrefix}
            onChange={(e) => setInvoicePrefix(e.target.value)}
            placeholder="INV-"
            hint="Bills are numbered INV-1, INV-2, …"
          />
        </div>

        <label className="flex cursor-pointer items-start gap-3 rounded-lg border border-white/10 bg-white/[0.06] p-3 backdrop-blur-sm">
          <input
            type="checkbox"
            checked={taxInclusive}
            onChange={(e) => setTaxInclusive(e.target.checked)}
            className="mt-0.5 h-4 w-4 shrink-0 accent-accent"
          />
          <span>
            <span className="block text-body text-fg-primary">
              My prices already include GST
            </span>
            <span className="mt-0.5 block text-label text-fg-tertiary">
              Typical for kirana and retail counters. You can change this later in
              Settings.
            </span>
          </span>
        </label>

        <button type="submit" disabled={!canSubmit} className={PRIMARY}>
          {busy ? 'Creating store…' : 'Create store and start billing'}
        </button>

        {currentUser?.phone && (
          <p className="text-center text-label text-fg-tertiary">
            Signed in as {currentUser.phone}
          </p>
        )}
      </form>
    </OnboardingCard>
  );
}
